import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import OwnerSidebar from "@/components/navigation/OwnerSidebar";
import AppFooter from "@/components/layout/AppFooter";
import { toast } from "sonner";
import { Plus, Loader2, FolderOpen } from "lucide-react";
import axios from "axios";
import { canCreateDienstplan, canManagePlan } from "@/lib/roles";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const MEMBER_SELECTED_PLAN_KEY = "member_selected_plan_id";

const DashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [dienstplaene, setDienstpläne] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const createMode = new URLSearchParams(location.search).get("create") === "1";

  useEffect(() => {
    fetchDienstpläne();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (createMode) {
      if (!canCreateDienstplan(user)) {
        navigate("/dienstplan", { replace: true });
        return;
      }
      setCreateOpen(true);
      return;
    }
    if (dienstplaene.length > 0) {
      const savedPlanId = localStorage.getItem(MEMBER_SELECTED_PLAN_KEY);
      const preferredPlan =
        dienstplaene.find((plan) => String(plan.id) === String(savedPlanId)) ||
        dienstplaene.find((plan) => String(plan.id) === String(user?.dienstplan_id)) ||
        dienstplaene[0];
      if (canManagePlan(preferredPlan)) {
        navigate(`/dienstplan/${preferredPlan.id}`, { replace: true });
      }
    }
  }, [loading, createMode, dienstplaene, navigate, user]);

  const fetchDienstpläne = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dienstplan`);
      setDienstpläne(response.data);
    } catch (error) {
      console.error("Error fetching dienstplaene:", error);
      toast.error("Fehler beim Laden der Dienstpläne");
    } finally {
      setLoading(false);
    }
  };


  const handleCreateDienstplan = async (e) => {
    e.preventDefault();
    
    if (!newName.trim()) {
      toast.error("Bitte gib einen Namen ein");
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/api/dienstplan`, {
        name: newName,
        description: newDescription
      });
      await refreshUser();
      setDienstpläne([...dienstplaene, response.data]);
      setCreateOpen(false);
      setNewName("");
      setNewDescription("");
      toast.success("Dienstplan erstellt!");
      navigate(`/dienstplan/${response.data.id}`, { replace: true });
    } catch (error) {
      console.error("Error creating dienstplan:", error);
      toast.error("Fehler beim Erstellen des Dienstplans");
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Erfolgreich abgemeldet");
  };

  return (
    <div className="dashboard-layout" data-testid="dashboard-page">
      {/* Mobile Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      
      <OwnerSidebar
        user={user}
        sidebarOpen={sidebarOpen}
        activeMain="dashboard"
        plans={dienstplaene}
        onLogout={handleLogout}
      />

      {/* Main Content - Light */}
      <main className="dashboard-main">
        <div className="max-w-5xl mx-auto px-6 py-8 flex-1 w-full">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="font-heading text-2xl font-semibold text-[#212529]">Dienstplan</h1>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              {canCreateDienstplan(user) ? (
                <DialogTrigger asChild>
                  <Button className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold" data-testid="create-dienstplan-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Neuer Dienstplan
                  </Button>
                </DialogTrigger>
              ) : null}
              <DialogContent className="bg-white border-gray-200">
                <DialogHeader>
                  <DialogTitle className="font-heading text-[#212529]">Neuen Dienstplan erstellen</DialogTitle>
                  <DialogDescription className="text-gray-500">
                    Erstelle einen neuen Dienstplan für dein Team.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateDienstplan} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="dp-name" className="text-[#212529]">Name</Label>
                    <Input
                      id="dp-name"
                      placeholder="z.B. Wochenplan Restaurant"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="border-gray-300 focus:border-[#17c671]"
                      data-testid="dienstplan-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dp-desc" className="text-[#212529]">Beschreibung (optional)</Label>
                    <Textarea
                      id="dp-desc"
                      placeholder="Kurze Beschreibung des Dienstplans..."
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                      rows={3}
                      className="border-gray-300 focus:border-[#17c671]"
                      data-testid="dienstplan-desc-input"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="border-gray-300">
                      Abbrechen
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold"
                      disabled={creating}
                      data-testid="dienstplan-create-submit-btn"
                    >
                      {creating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Erstellen...
                        </>
                      ) : (
                        "Erstellen"
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#17c671]" />
            </div>
          ) : (
            <div className="empty-state animate-fade-in">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FolderOpen className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#212529] mb-2">
                Noch keine Dienstpläne
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                {canCreateDienstplan(user)
                  ? "Erstelle deinen ersten Dienstplan, um Schichten zu planen und dein Team einzuladen."
                  : "Dir ist aktuell kein Dienstplan mit Verwaltungsrechten zugeordnet."}
              </p>
              {canCreateDienstplan(user) ? (
                <Button 
                  onClick={() => setCreateOpen(true)}
                  className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ersten Dienstplan erstellen
                </Button>
              ) : null}
              </div>
          )}
        </div>
      </main>
      <AppFooter />
    </div>
  );
};

export default DashboardPage;
